import React, { useEffect, useState } from 'react';
import { getCierres, cerrarMensual, cerrarAnual, reabrirCierre } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ultimoDiaMes = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m, 0).toISOString().slice(0, 10);
};
const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const anoAnterior = () => String(new Date().getFullYear() - 1);

export default function CierrePeriodo() {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'administrador';

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo]   = useState('');

  const [mes,  setMes]  = useState(mesActual());
  const [ano,  setAno]  = useState(anoAnterior());
  const [notasMes, setNotasMes] = useState('');
  const [notasAno, setNotasAno] = useState('');
  const [working, setWorking]   = useState(false);

  const cargar = async () => {
    try { const { data } = await getCierres(); setData(data); setError(''); }
    catch (err) { setError(err.response?.data?.error || 'Error al cargar'); }
  };
  useEffect(() => { cargar(); }, []);

  const onCerrarMes = async () => {
    const fecha_corte = ultimoDiaMes(mes);
    if (!window.confirm(`¿Cerrar mes hasta ${fecha_corte}?\nNo se podrán crear ni modificar comprobantes con fecha <= ${fecha_corte}.`)) return;
    setWorking(true); setError(''); setInfo('');
    try {
      await cerrarMensual({ fecha_corte, notas: notasMes });
      setInfo(`✓ Periodo cerrado al ${fecha_corte}`);
      setNotasMes('');
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cerrar');
    } finally { setWorking(false); }
  };

  const onCerrarAno = async () => {
    if (!window.confirm(`¿Cerrar el año ${ano}?\nEsto genera el asiento de cancelación de ingresos y gastos contra utilidad/pérdida del ejercicio. La operación es reversible solo por el administrador.`)) return;
    setWorking(true); setError(''); setInfo('');
    try {
      const { data } = await cerrarAnual({ ano: parseInt(ano), notas: notasAno });
      setInfo(`✓ Año ${ano} cerrado — comprobante ${data.comprobante.numero} (${data.lineas} líneas, utilidad ${data.utilidad >= 0 ? '+' : ''}${Number(data.utilidad).toLocaleString('es-CO')})`);
      setNotasAno('');
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cerrar año');
    } finally { setWorking(false); }
  };

  const onReabrir = async (c) => {
    if (!window.confirm(`¿Reabrir el cierre ${c.tipo} del ${c.fecha_corte}?\n${c.tipo === 'anual' ? 'El comprobante de cancelación quedará anulado.' : 'Los movimientos del periodo volverán a ser editables.'}`)) return;
    setWorking(true); setError(''); setInfo('');
    try {
      await reabrirCierre(c.id);
      setInfo(`✓ Cierre del ${c.fecha_corte} reabierto`);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reabrir');
    } finally { setWorking(false); }
  };

  return (
    <div style={S.container}>
      <div style={S.header}>
        <h1 style={S.title}>Cierre de periodo</h1>
        <p style={S.sub}>Bloquea las fechas anteriores y genera el asiento de cancelación al cerrar el año.</p>
      </div>

      {error && <div style={S.error}>{error}</div>}
      {info  && <div style={S.info}>{info}</div>}

      <div style={S.lockCard}>
        <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Fecha actual de bloqueo</div>
        <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'monospace', color: data?.fecha_bloqueo ? '#0f172a' : '#94a3b8', marginTop: 4 }}>
          {data?.fecha_bloqueo || 'sin bloqueo'}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
          {data?.fecha_bloqueo
            ? `No se admiten movimientos con fecha ≤ ${data.fecha_bloqueo}`
            : 'Cualquier fecha es editable'}
        </div>
      </div>

      <div style={S.row2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}>Cierre mensual</h3>
          <p style={S.cardNote}>Bloquea el periodo después de revisión. No genera asientos.</p>
          <label style={S.label}>Mes a cerrar</label>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={S.input} />
          <span style={S.hint}>Se cerrará hasta {ultimoDiaMes(mes)}</span>
          <label style={{ ...S.label, marginTop: 12 }}>Notas (opcional)</label>
          <input value={notasMes} onChange={e => setNotasMes(e.target.value)} style={S.input} placeholder="ej. Revisado por contador" />
          <button onClick={onCerrarMes} disabled={working} style={{ ...S.btnPri, marginTop: 14 }}>
            Cerrar mes
          </button>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}>Cierre anual</h3>
          <p style={S.cardNote}>
            Genera el asiento de cancelación de cuentas de resultado (clases 4-7) contra
            utilidad/pérdida del ejercicio (360505 / 361005). Bloquea hasta el 31/dic.
          </p>
          <label style={S.label}>Año</label>
          <input type="number" min="2020" max="2099" value={ano} onChange={e => setAno(e.target.value)} style={S.input} />
          <span style={S.hint}>Se cerrará hasta {ano}-12-31</span>
          <label style={{ ...S.label, marginTop: 12 }}>Notas (opcional)</label>
          <input value={notasAno} onChange={e => setNotasAno(e.target.value)} style={S.input} placeholder="ej. Cierre fiscal definitivo" />
          <button onClick={onCerrarAno} disabled={working} style={{ ...S.btnPri, marginTop: 14, background: '#dc2626' }}>
            Cerrar año {ano}
          </button>
        </div>
      </div>

      <h3 style={{ fontSize: 16, color: '#0f172a', marginTop: 24, marginBottom: 8 }}>Histórico de cierres</h3>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Fecha de corte</th>
            <th style={S.th}>Tipo</th>
            <th style={S.th}>Comprobante</th>
            <th style={S.th}>Cerrado por</th>
            <th style={S.th}>Notas</th>
            {esAdmin && <th style={S.th}>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {!data ? <tr><td colSpan={esAdmin ? 6 : 5} style={{ ...S.td, textAlign: 'center', padding: 30 }}>Cargando…</td></tr>
            : data.cierres.length === 0
              ? <tr><td colSpan={esAdmin ? 6 : 5} style={{ ...S.td, textAlign: 'center', padding: 30, color: '#94a3b8' }}>Sin cierres registrados</td></tr>
              : data.cierres.map(c => (
                <tr key={c.id}>
                  <td style={{ ...S.td, fontFamily: 'monospace' }}>{c.fecha_corte}</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: c.tipo === 'anual' ? '#fee2e2' : '#dbeafe', color: c.tipo === 'anual' ? '#991b1b' : '#1d4ed8' }}>
                      {c.tipo}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{c.comprobante_numero || '—'}</td>
                  <td style={S.td}>{c.cerrado_por_nombre || '—'}</td>
                  <td style={S.td}>{c.notas || ''}</td>
                  {esAdmin && (
                    <td style={S.td}>
                      <button onClick={() => onReabrir(c)} disabled={working} style={S.btnSec}>Reabrir</button>
                    </td>
                  )}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header:    { marginBottom: 18 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13 },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  info:      { background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  lockCard:  { background: '#fff', borderRadius: 8, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '4px solid #3b82f6' },
  row2:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 14, marginBottom: 14 },
  card:      { background: '#fff', borderRadius: 8, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardTitle: { margin: 0, fontSize: 16, color: '#0f172a' },
  cardNote:  { fontSize: 12, color: '#64748b', margin: '4px 0 12px' },
  label:     { display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 },
  input:     { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  hint:      { fontSize: 11, color: '#94a3b8' },
  btnPri:    { padding: '9px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnSec:    { padding: '4px 10px', background: '#64748b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  table:     { width: '100%', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: 8, overflow: 'hidden' },
  th:        { background: '#f1f5f9', padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0' },
  td:        { padding: '8px 12px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  badge:     { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },
};
